import { useState, type FormEvent } from 'react'
import { Button } from '@floorfile/ui'
import { useCopy } from '@floorfile/i18n'
import { Link, useLocation, useNavigate } from 'react-router-dom'
import { useAuth } from '@/lib/auth'
import { TurnstileWidget, turnstileEnabled } from '@/lib/turnstile'
import { registrationOpen } from './RegisterPage'
import { appCopy } from '../copy'

export function LoginPage() {
  const copy = useCopy(appCopy)
  const { login, completeMfa } = useAuth()
  const navigate = useNavigate()
  const location = useLocation()
  const returnTo = (location.state as { from?: { pathname?: string; search?: string } } | undefined)?.from
  const afterLogin = returnTo?.pathname ? `${returnTo.pathname}${returnTo.search ?? ''}` : '/'
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [mfaToken, setMfaToken] = useState<string | undefined>(undefined)
  const [code, setCode] = useState('')
  const [error, setError] = useState<string | undefined>(undefined)
  const [busy, setBusy] = useState(false)
  const [turnstileToken, setTurnstileToken] = useState<string | undefined>(undefined)
  // Turnstile tokens are single-use; remount the widget after a failed
  // submit so the retry presents a fresh token.
  const [turnstileEpoch, setTurnstileEpoch] = useState(0)

  async function onSubmit(event: FormEvent) {
    event.preventDefault()
    setError(undefined)
    setBusy(true)
    try {
      if (mfaToken) {
        await completeMfa(mfaToken, code)
        navigate(afterLogin, { replace: true })
        return
      }
      const result = await login(email, password, turnstileToken)
      if (result.mfaRequired) {
        setMfaToken(result.mfaToken)
      } else {
        navigate(afterLogin, { replace: true })
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : copy.login.failed)
      setTurnstileToken(undefined)
      setTurnstileEpoch((epoch) => epoch + 1)
    } finally {
      setBusy(false)
    }
  }

  return (
    <main className="flex min-h-screen items-center justify-center bg-background p-4">
      <form onSubmit={(e) => void onSubmit(e)} className="w-full max-w-sm space-y-4 rounded-lg border bg-card p-6">
        <div>
          <h1 className="text-xl font-semibold">{copy.shell.brand}</h1>
          <p className="text-sm text-muted-foreground">
            {mfaToken ? copy.login.mfaPrompt : copy.login.prompt}
          </p>
        </div>

        {!mfaToken && (
          <>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{copy.authCommon.email}</span>
              <input
                type="email"
                required
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                autoComplete="username"
              />
            </label>
            <label className="block text-sm">
              <span className="mb-1 block text-muted-foreground">{copy.authCommon.password}</span>
              <input
                type="password"
                required
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full rounded-md border bg-background px-3 py-2"
                autoComplete="current-password"
              />
            </label>
          </>
        )}

        {mfaToken && (
          <label className="block text-sm">
            <span className="mb-1 block text-muted-foreground">{copy.authCommon.authenticationCode}</span>
            <input
              inputMode="numeric"
              required
              value={code}
              onChange={(e) => setCode(e.target.value)}
              className="w-full rounded-md border bg-background px-3 py-2 tracking-widest"
              autoComplete="one-time-code"
            />
          </label>
        )}

        {!mfaToken && <TurnstileWidget key={turnstileEpoch} onToken={setTurnstileToken} />}

        {!mfaToken && (
          <p className="text-right text-sm">
            <Link to="/forgot-password" className="text-primary hover:underline">
              {copy.login.forgotPassword}
            </Link>
          </p>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <Button className="w-full"
 type="submit"
 disabled={busy || (turnstileEnabled && !mfaToken && !turnstileToken)}

 >
          {busy ? copy.login.signingIn : mfaToken ? copy.login.verifyCode : copy.login.signIn}
        </Button>

        {!mfaToken && registrationOpen && (
          <p className="text-center text-sm text-muted-foreground">
            {copy.login.newPlant}{' '}
            <Link to="/register" className="text-primary hover:underline">
              {copy.login.createCompany}
            </Link>
          </p>
        )}
      </form>
    </main>
  )
}
